import React from "react";
import { ErrorMessage, LoadingIndicator } from "@jsq/ds/core";
import { WorkflowMode } from "../../common/enums";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { UpdatePositionsWithoutPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithoutPaymentPrefPositionList";
import { UpdatePositionsWithPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";
import { usePaymentPrefCreationForAccountWorkflowController } from "./controller";
import { type PaymentPrefCreationForAccountWorkflowProps } from "./types";

export const PaymentPrefCreationForAccountWorkflow: React.FC<
  PaymentPrefCreationForAccountWorkflowProps
> = ({ accountId, onExit, onSuccess, sourcePaymentPrefId, arena }) => {
  const {
    dataLoading,
    dataError,
    availablePaymentMethods,
    existingAccountPaymentPrefsData,
    associatePaymentPrefType,
    paymentPrefIdForContext,
    prefixComponents,
  } = usePaymentPrefCreationForAccountWorkflowController({
    accountId,
    sourcePaymentPrefId,
  });

  if (dataLoading) {
    return <LoadingIndicator />;
  }

  if (dataError) {
    return <ErrorMessage>Failed to load</ErrorMessage>;
  }

  return (
    <ConsolidatedPaymentPrefProvider
      availablePaymentMethods={availablePaymentMethods}
      existingPaymentPrefs={existingAccountPaymentPrefsData?.account?.paymentPrefs}
      initialAssociatePaymentPref={associatePaymentPrefType}
      sourcePaymentPrefId={paymentPrefIdForContext}
      currentPosition={{ accountId, id: "", investmentEntityId: "", investmentEntityName: "" }}
      ListOfPositionsToUpdateComponent={
        associatePaymentPrefType ===
        AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
          ? UpdatePositionsWithPaymentPrefPositionList
          : UpdatePositionsWithoutPaymentPrefPositionList
      }
      workflowMode={WorkflowMode.Create}
      accountName={existingAccountPaymentPrefsData?.account?.name}
      arena={arena}
    >
      <BaseFullScreenTakeover title="Update payment instructions" onExit={onExit}>
        <BasePaymentPrefWorkflow
          workflowMode={WorkflowMode.Create}
          prefixComponents={prefixComponents}
          suffixComponents={[PaymentPrefConfirmation]}
          onExit={onExit}
          onSuccess={onSuccess}
        />
      </BaseFullScreenTakeover>
    </ConsolidatedPaymentPrefProvider>
  );
};

export type { PaymentPrefCreationForAccountWorkflowProps };
